import { useQuery } from '@tanstack/react-query';

import type { QuizsetFilterCriteria } from '@/api/gen/_model/quizsetFilterCriteria';
import { searchQuizsetsByCursor } from '@/api/gen/quiz/quiz.gen';
import { useCountryCode } from '@/shared/hooks';

/**
 * useCategoryQuizzes - 카테고리(학년/과목/출판사)가 동일한 퀴즈 목록을 가져오는 hook
 *
 * @description
 * POST /quizsets/search/cursor API를 호출하여
 * 현재 퀴즈와 동일한 카테고리의 다른 퀴즈 목록을 가져옵니다.
 * React Query의 useQuery를 사용하여 데이터를 캐싱하고 관리합니다.
 *
 * @param criteria - 퀴즈 필터 조건 (schoolGrades, publishers, schoolType 등)
 * @param subject - 과목 필터
 * @param currentQuizId - 현재 퀴즈 ID (결과에서 제외하기 위해)
 * @returns React Query result object
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useCategoryQuizzes({
 *   schoolGrades: ['중1'],
 *   publishers: ['비상'],
 * }, '수학', 'current-quiz-id');
 * ```
 */
export const useCategoryQuizzes = (
  criteria: Partial<QuizsetFilterCriteria>,
  subject?: string,
  currentQuizId?: string,
) => {
  const { countryCode: viewerCountryCode } = useCountryCode();
  // 추천 국가코드는 "현재 퀴즈의 국가"(criteria.countryCode) 우선, 없으면 뷰어 국가로 fallback.
  // Googlebot 은 주로 US IP 에서 크롤링하므로 뷰어 기준이면 KR 퀴즈의 연관 링크가 비어
  // orphan 이 된다. 퀴즈 기준으로 맞춰 내부 링크/색인을 안정화한다.
  // 퀴즈 countryCode 는 서버 스키마상 빈 문자열('')도 가능하므로 `??` 가 아닌 `||` 로
  // 빈 문자열까지 뷰어 fallback 시킨다(`??` 면 ''에서 fallback 안 돼 추천이 사라짐).
  const countryCode = criteria.countryCode || viewerCountryCode;

  return useQuery({
    queryKey: [
      'category-quizzes',
      criteria,
      subject,
      countryCode,
      currentQuizId,
    ],
    queryFn: async () => {
      if (!countryCode) {
        throw new Error('countryCode is not available');
      }

      const response = await searchQuizsetsByCursor({
        // 이 영역은 퀴즈만 보여준다 — 폴더는 자료실 목록에서만 섞인다.
        contentType: 'QUIZSET',
        order: 'RECENTLY',
        categoryFilters: {
          criteria: {
            ...criteria,
            countryCode,
          },
          // Public Page와 동일한 로직: 4자리면 subSubject, 2자리면 subject
          ...(subject?.length === 4
            ? { subSubject: subject.slice(2) }
            : subject
            ? { subject }
            : {}),
        },
      });

      const data = response.data
        .filter(quiz => quiz.id !== currentQuizId)
        .slice(0, 10);

      return {
        ...response,
        data,
      };
    },
    enabled: !!countryCode && Object.keys(criteria).length > 0,
    staleTime: 0,
    throwOnError: false, // 에러를 throw하지 않고 isError 상태로 처리
    retry: 1, // 1회만 재시도
  });
};
